import CommonInput from "@/shared/components/ui/Input/CommonInput";
import MarkdownEditor from "@/shared/components/ui/Input/MarkdownEditor";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import { Mail, X } from "lucide-react";
import React, { useEffect, useState, useRef } from "react";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import Loader from "@/shared/components/ui/Loaders/Loader";
import {
  useDraftRelationshipEmail,
  useFindRelationshipEmail,
  useRelationshipMappingDetail,
  useSaveRelationshipContactDetails,
  useSendAccountEmail,
  useSendRelationshipEmail,
} from "../hooks/useAccountRelationship";

import { formatEmailSpacing } from "@/shared/utils/markdownUtils";
import { showError } from "@/shared/utils/toast";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseCc = (value) => [
  ...new Set(
    String(value || "")
      .split(/[;,]/)
      .map((email) => email.trim().toLowerCase())
      .filter(Boolean),
  ),
];

const ShowDraftModal = ({ isOpen, onClose, contact }) => {
  const relationshipMappingId = contact?.relationship_mapping_id;
  const isManualRecipientMode = !relationshipMappingId;
  const [recipient, setRecipient] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [draftRequested, setDraftRequested] = useState(false);
  const [isEditingRecipient, setIsEditingRecipient] = useState(false);
  const sendRequest = useRef(null);
  const [sendProblem, setSendProblem] = useState(null);
  const { data: relationshipDetail, isLoading: isDetailLoading } =
    useRelationshipMappingDetail({
      relationshipMappingId,
      enabled: isOpen,
    });
  const { mutate: draftEmail, isPending: isDrafting } =
    useDraftRelationshipEmail();
  const { mutate: findRelationshipEmail, isPending: isFindingEmail } =
    useFindRelationshipEmail();
  const { mutate: saveContactDetails, isPending: isSavingContactDetails } =
    useSaveRelationshipContactDetails();
  const { mutate: sendEmail, isPending: isSending } =
    useSendRelationshipEmail();
  const { mutate: sendAccountEmail, isPending: isSendingAccountEmail } =
    useSendAccountEmail();

  useEffect(() => {
    if (!isOpen) return;
    setRecipient("");
    setCc("");
    setSubject("");
    setBody("");
    setDraftRequested(false);
    setIsEditingRecipient(false);
    sendRequest.current = null;
    setSendProblem(null);
  }, [isOpen, relationshipMappingId]);

  useEffect(() => {
    const savedEmail = String(
      relationshipDetail?.crm_contact?.email || "",
    ).trim();
    if (
      !isOpen ||
      !relationshipMappingId ||
      isDetailLoading ||
      draftRequested ||
      !savedEmail
    ) {
      return;
    }

    setRecipient(savedEmail);
    setDraftRequested(true);
    draftEmail(
      { relationshipMappingId },
      {
        onSuccess: (draft) => {
          setRecipient(draft.recipient || "");
          setSubject(draft.subject || "");
          setBody(formatEmailSpacing(draft.body) || "");
        },
      },
    );
  }, [
    draftEmail,
    draftRequested,
    isDetailLoading,
    isOpen,
    relationshipDetail?.crm_contact?.email,
    relationshipMappingId,
  ]);

  const handleSaveRecipient = () => {
    const email = recipient.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      showError("Enter a valid recipient email address.");
      return;
    }

    if (isManualRecipientMode) {
      setRecipient(email);
      setDraftRequested(true);
      setIsEditingRecipient(false);
      return;
    }

    saveContactDetails(
      { relationshipMappingId, email },
      {
        onSuccess: () => {
          setRecipient(email);
          setIsEditingRecipient(false);
          setDraftRequested(true);
          draftEmail(
            { relationshipMappingId },
            {
              onSuccess: (draft) => {
                setRecipient(draft.recipient || email);
                setSubject(draft.subject || "");
                setBody(formatEmailSpacing(draft.body) || "");
              },
            },
          );
        },
      },
    );
  };

  const savedRecipient = String(
    relationshipDetail?.crm_contact?.email || "",
  ).trim();

  const handleFindEmail = () => {
    if (!relationshipMappingId || isFindingEmail) return;

    findRelationshipEmail(
      { relationshipMappingId },
      {
        onSuccess: (result) => {
          if (!result?.found || !result.email) {
            showError("No work email was found for this contact.");
            return;
          }
          setRecipient(result.email);
          setIsEditingRecipient(Boolean(savedRecipient));
        },
      },
    );
  };

  const hasSavedRecipient = Boolean(savedRecipient || draftRequested);
  const isDraftReady = hasSavedRecipient && !isEditingRecipient;

  const handleSend = () => {
    const ccRecipients = parseCc(cc).filter(
      (email) => email !== recipient.trim().toLowerCase(),
    );
    const invalidCc = ccRecipients.find((email) => !EMAIL_RE.test(email));
    if (invalidCc) {
      showError(`Enter a valid CC email address: ${invalidCc}`);
      return;
    }
    if (ccRecipients.length > 20) {
      showError("CC supports up to 20 recipients.");
      return;
    }

    const fingerprint = JSON.stringify([recipient, subject, body, ccRecipients]);
    if (!sendRequest.current || sendRequest.current.fingerprint !== fingerprint) {
      sendRequest.current = { fingerprint, id: window.crypto?.randomUUID?.() || `${Date.now()}` };
    }
    const requestId = sendRequest.current.id;
    setSendProblem(null);
    const onSendError = error => {
      const details = error?.response?.data;
      setSendProblem(details?.allowedAt
        ? `Sending is delayed (${details.reason || 'mailbox limit'}). Next allowed: ${new Date(details.allowedAt).toLocaleString()}`
        : details?.error || 'Email could not be confirmed. Please check its status before sending again.');
    };

    if (isManualRecipientMode) {
      sendAccountEmail(
        {
          companyName: contact?.company_name,
          recipient: recipient.trim().toLowerCase(),
          requestId,
          subject,
          body,
          cc: ccRecipients,
        },
        { onSuccess: onClose, onError: onSendError },
      );
      return;
    }

    sendEmail(
      {
        relationshipMappingId,
        requestId,
        subject,
        body,
        cc: ccRecipients,
      },
      { onSuccess: onClose, onError: onSendError },
    );
  };

  const isPreparing = isDetailLoading || isDrafting || isSavingContactDetails;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      width="900px"
      height="100%"
      className="p-6"
      expandable={true}
      defaultMaximized={true}
    >
      <div className="flex flex-col gap-4 w-full h-[calc(100dvh-150px)]">
        {sendProblem && <p role="alert" className="rounded bg-amber-50 p-3 text-sm text-amber-800">{sendProblem}</p>}
        <div
          className="flex items-start self-end justify-end hover:bg-gray-200 dark:hover:bg-gray-800 p-1 rounded-full w-fit cursor-pointer transition-colors"
          onClick={onClose}
        >
          <X size={14} className="text-gray-800 dark:text-gray-200" />
        </div>
        <div className="flex justify-between items-center">
          <div className="flex gap-2 items-center">
            <Mail size={18} className="text-gray-800 dark:text-gray-200" />
            <h6 className="text-sm font-medium text-gray-800 dark:text-gray-100">Draft Email</h6>
          </div>
        </div>

        {isPreparing ? (
          <div className="flex min-h-[460px] items-center justify-center">
            <Loader
              text={
                isSavingContactDetails ? "Saving email..." : "Drafting email..."
              }
            />
          </div>
        ) : (
          <>
            {isDraftReady ? (
              <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  To: {recipient}
                </p>
                <div className="flex items-center gap-3">
                  {!isManualRecipientMode && (
                    <button
                      type="button"
                      className="text-[11px] font-medium text-secondary hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={handleFindEmail}
                      disabled={isFindingEmail}
                    >
                      {isFindingEmail ? "Finding..." : "Refresh work email"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="text-[11px] font-medium text-secondary hover:underline"
                    onClick={() => {
                      setRecipient(savedRecipient || recipient);
                      setIsEditingRecipient(true);
                    }}
                  >
                    Change
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <CommonInput
                  placeholder="Enter recipient email"
                  type="email"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  required
                  wrapperClassName="flex-1"
                />
                {!isManualRecipientMode && (isEditingRecipient || !recipient.trim()) && (
                  <button
                    type="button"
                    className="shrink-0 rounded-lg border border-secondary px-3 py-2 text-[11px] font-medium text-secondary hover:bg-secondary/5 disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={handleFindEmail}
                    disabled={isFindingEmail}
                  >
                    {isFindingEmail ? "Finding..." : "Find work email"}
                  </button>
                )}
                {isEditingRecipient && (
                  <button
                    type="button"
                    className="px-2 py-2 text-[11px] text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    onClick={() => {
                      setRecipient(savedRecipient || recipient);
                      setIsEditingRecipient(false);
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            )}
            {isDraftReady && (
              <>
                <CommonInput
                  placeholder="CC (optional, separate multiple emails with commas)"
                  type="text"
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                />
                <CommonInput
                  placeholder="Enter subject"
                  type="text"
                  editable={true}
                  className="border border-gray-400 dark:border-gray-700 bg-white dark:bg-gray-800 opacity-80 p-4 rounded-lg text-xs font-bold text-gray-800 dark:text-gray-100"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
                <MarkdownEditor value={body} setValue={setBody} height="400px" />
              </>
            )}
          </>
        )}

        {!isPreparing && (
          <PrimaryButton
            startContent={false}
            onClick={isDraftReady ? handleSend : handleSaveRecipient}
            isLoading={isSending || isSendingAccountEmail || isSavingContactDetails}
            disabled={
              isSending ||
              isSendingAccountEmail ||
              !recipient.trim() ||
              (isDraftReady && (!subject.trim() || !body.trim()))
            }
            className="w-fit justify-end self-end"
          >
            {isDraftReady
              ? "Send Email"
              : hasSavedRecipient
                ? "Update & Draft"
                : "Save & Draft"}
          </PrimaryButton>
        )}
      </div>
    </CustomModal>
  );
};

export default ShowDraftModal;
